import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import Utils from "models/helper/Utils";
import RSVP from "rsvp";
import { getNumberOrUndefined } from 'chartist';
import VoiceActivityDetectionManager from "models/audioEditor/VoiceActivityDetectionManager";

const SOUND_GROUP_TO_SOUND_CLASSES = {
    SPEECH: ["Speech"],
    SPEECH_RELATED_SOUNDS: [
        "Conversation",
        "Narration, monologue",
        "Whispering",
        "Speech synthesizer"
    ],
    BACKGROUND_NOISES: [
        "Silence",
        "Environmental noise",
        "Static",
        "Mains hum"
    ],
    HUMAN_SOUNDS: [
        "Laughter",
        "Giggle",
        "Chuckle",
        "Coughing",
        "Sneezing",
        "Throat clearing",
        "Breathing",
        "Sniffing"
    ],
    EMOTIONAL_OR_REACTIVE_SOUNDS: [
        "Crying, sobbing",
        "Whimper",
        "Yelling",
        "Screaming"
    ],
    AUDITORY_CUES_FOR_VIDEO_CONTENT: [
        "Applause",
        "Cheering",
        "Footsteps"
    ],
    DISTURBANCES_AND_UNWANTED_NOISES: [
        "Echo",
        "Reverberation",
        "Background music",
        "Music overlap"
    ],
    SPECIFIC_CONTENT_TYPES: [
        "Music",
        "Singing",
        "Musical instrument",
        "Animal",
        "Dog",
        "Bark",
        "Cat",
        "Purr",
        "Meow"
    ],
    TECHNICAL_SOUNDS: [
        "Beep",
        "Ringtone",
        "Alarm",
        "Camera shutter",
        "Typing",
        "Clicking"
    ],
    MISCELLANEOUS_SOUNDS:[
        "Tick",
        "Creak"
    ]
};

const ALLOWED_SOUND_CLASSES = Object.values(SOUND_GROUP_TO_SOUND_CLASSES).reduce((acc, arrayOfSoundClasses)=>{return acc.concat(arrayOfSoundClasses)},[]);
const SOUND_GROUPS = Object.keys(SOUND_GROUP_TO_SOUND_CLASSES).reduce((acc, sound_group_key)=>{acc[sound_group_key] = sound_group_key; return acc},{});


const SequencerNodeUtils =  Backbone.Model.extend({
    idAttribute: "id",

    constructor: function(attributes, options) {
        Backbone.Model.apply(this, [attributes, options]);

    },

    getArrayOfSequencerNodesFromStartToEnd : function(startNode, endNode){
        const arrayOfSequencerNodes = [];
        let reachEndNode = false;
        let cursorNode = startNode;
        while(!reachEndNode && cursorNode){
            arrayOfSequencerNodes.push(cursorNode);
            if(cursorNode === endNode){
                reachEndNode = true;
            }
            cursorNode = cursorNode.getNext();
        }
        return arrayOfSequencerNodes;
    },

    getPreviousWordSequencerNodeAtBeginningOfSentence : function(sequencerNode, nTimes, returnCurrentNodeIfNoNodeIsMatching){
        const previousWordSequencerAtEndOfSentence = this.getPreviousWordSequencerNodeAtEndOfSentence(sequencerNode, nTimes, returnCurrentNodeIfNoNodeIsMatching);
        if(previousWordSequencerAtEndOfSentence && previousWordSequencerAtEndOfSentence.getNext() && sequencerNode !=  previousWordSequencerAtEndOfSentence.getNext()){
            return previousWordSequencerAtEndOfSentence.getNext();
        }else{
            return previousWordSequencerAtEndOfSentence;
        }
    },

    getPreviousWordSequencerNodeAtEndOfSentence : function(sequencerNode, nTimes, returnCurrentNodeIfNoNodeIsMatching){
        const previousWordSequencer = this.getPreviousNWordSequencerNode(sequencerNode, nTimes, returnCurrentNodeIfNoNodeIsMatching);
        const isAWordNodeAtAtEndOfSentenceFunction = (function(node){
            if(!node){
                return null;
            }
            return Utils.getInstance().isWordSequencerNodeInstance(node) &&  Utils.getInstance().isValidEndOfSentence(node.getContent())
        })
        if(previousWordSequencer){
            return previousWordSequencer.getPreviousNodeMatchingCondition(isAWordNodeAtAtEndOfSentenceFunction.bind(previousWordSequencer), 0, returnCurrentNodeIfNoNodeIsMatching);
        }else{
            return null;
        }

    },

    getSequencerNodeCidToSequencerNodeMap: function(sequencerNodeToStartAt, sequencerNodeToStopAt, sequencerNodeCidToNodeMap){
        if(!sequencerNodeCidToNodeMap){
            sequencerNodeCidToNodeMap = {}
        }
        let sequencerNodeToClone = sequencerNodeToStartAt;
        let lastSequencerNodeToClone = null;
        while(sequencerNodeToClone){
            sequencerNodeCidToNodeMap[sequencerNodeToClone.cid] = sequencerNodeToClone;
            if(sequencerNodeToClone === sequencerNodeToStopAt || !sequencerNodeToClone.getNext()){
                lastSequencerNodeToClone = sequencerNodeToClone;
                sequencerNodeToClone = null;
            }else{
                sequencerNodeToClone = sequencerNodeToClone.getNext();
            }
        }
        return sequencerNodeCidToNodeMap;

    },

    getPreviousNWordSequencerNode : function(sequencerNode, nTimes, returnCurrentNodeIfNoNodeIsMatching){
        const isAWordNodeFunction = (function(node){
            return Utils.getInstance().isWordSequencerNodeInstance(node);
        })
        return sequencerNode.getPreviousNodeMatchingCondition(isAWordNodeFunction.bind(sequencerNode), nTimes, returnCurrentNodeIfNoNodeIsMatching);

    },

    getNextNWordSequencerNode : function(sequencerNode, nTimes, returnCurrentNodeIfNoNodeIsMatching){
        const isAWordNodeFunction = (function(node){
            return Utils.getInstance().isWordSequencerNodeInstance(node);
        })
        return sequencerNode.getNextNodeMatchingCondition(isAWordNodeFunction.bind(sequencerNode), nTimes, returnCurrentNodeIfNoNodeIsMatching);

    },

    getNextWordSequencerNodeAtEndOfSentence : function(sequencerNode, nTimes, returnCurrentNodeIfNoNodeIsMatching){
        const nextWordSequencer = this.getNextNWordSequencerNode(sequencerNode, nTimes, returnCurrentNodeIfNoNodeIsMatching);
        const isAWordNodeAtAtEndOfSentenceFunction = (function(node){
            if(!node){
                return null;
            }
            return Utils.getInstance().isWordSequencerNodeInstance(node) &&  Utils.getInstance().isValidEndOfSentence(node.getContent())
        })
        if(nextWordSequencer){
            return nextWordSequencer.getNextNodeMatchingCondition(isAWordNodeAtAtEndOfSentenceFunction.bind(nextWordSequencer), 0, returnCurrentNodeIfNoNodeIsMatching);
        }else{
            return null;
        }

    },
    
    getNumberOfClassifications: function(audioCategories){
        return audioCategories.reduce((acc,r)=>{ return acc+ r.classifications.length}, 0);
    },

    doesSequencerNodeMatchesAudioClassCondition :function(sequencerNode, audioClassConditionFunction, returnIfNoClassAvailable){
        const audioCategories = sequencerNode.getClassifiedAudioCategories();
        if(audioCategories && audioCategories.length > 0 && this.getNumberOfClassifications(audioCategories) > 0){
            return audioCategories.filter(audioClassConditionFunction).length > 0
        }else{
            if(returnIfNoClassAvailable){
                return true;
            }else{
                return false;
            }
        }
        return false;
    },

    getSoundClassesForSoundGroups : function(arrayOfSoundGroups){
        return arrayOfSoundGroups.reduce((acc, soundGroup)=>{
            const soundClasses = SOUND_GROUP_TO_SOUND_CLASSES[soundGroup];
            if(soundClasses && soundClasses.length){
                return acc.concat(soundClasses);
            }
            return acc;
        },[]);
    },

    doesSequencerNodeContainsAnyOfAudioClasses :function(sequencerNode, arrayOfAudioClasses, returnIfNoClassAvailable,minTreshold){
        const audioClassConditionFunction = (audioClassificationAtTimestamp)=>{
            if(audioClassificationAtTimestamp && audioClassificationAtTimestamp.classifications){
                for(let i =0; i < audioClassificationAtTimestamp.classifications.length; i++){
                    if(arrayOfAudioClasses.includes(audioClassificationAtTimestamp.classifications[i].categoryName) && (!minTreshold || audioClassificationAtTimestamp.classifications[i].categoryName >= minTreshold)){
                        return true;
                    }
                }
                return false;
            }else{
                return false;
            }
        }
        return this.doesSequencerNodeMatchesAudioClassCondition(sequencerNode, audioClassConditionFunction, returnIfNoClassAvailable)
    },

    doesSequencerNodeOnlyContainsOneOfAudioClasses :function(sequencerNode, arrayOfAudioClasses, returnIfNoClassAvailable, minTreshold){
        const audioClassConditionFunction = (audioClassificationAtTimestamp)=>{
            if(audioClassificationAtTimestamp && audioClassificationAtTimestamp.classifications){
                for(let i =0; i < audioClassificationAtTimestamp.classifications.length; i++){
                    if(!arrayOfAudioClasses.includes(audioClassificationAtTimestamp.classifications[i].categoryName) && (!minTreshold || audioClassificationAtTimestamp.classifications[i].categoryName >= minTreshold)){
                        return true;
                    }
                }
                return false;
            }else{
                return false;
            }
        }
        return this.doesSequencerNodeMatchesAudioClassCondition(sequencerNode, audioClassConditionFunction, returnIfNoClassAvailable)
    },

    doesSequencerNodeContainsSpeechOrOtherHumanSounds :function(sequencerNode){
        return this.doesSequencerNodeContainsAnyOfAudioClasses(sequencerNode, this.getSoundClassesForSoundGroups([SOUND_GROUPS.SPEECH, SOUND_GROUPS.SPEECH_RELATED_SOUNDS, SOUND_GROUPS.HUMAN_SOUNDS]), true)
    },

    doesSequencerNodeOnlyContainsBackgroundNoise :function(sequencerNode){
        return this.doesSequencerNodeContainsAnyOfAudioClasses(sequencerNode, this.getSoundClassesForSoundGroups([SOUND_GROUPS.BACKGROUND_NOISES]), true)
    },

    doesSequenerNodeContainsVoiceActivity : function(sequencerNode, returnTrueIfVoiceActivityInfo, vadConfigType){
        const vadManager = VoiceActivityDetectionManager.getInstance();
        const configType = vadConfigType || vadManager.getVadConfigs().BALANCED;

        if(!sequencerNode.getVoiceAcitivityInfo(configType)){
            return !!returnTrueIfVoiceActivityInfo;
        }
        return sequencerNode.containsVoiceActivity(configType);
    },

    /**
     * Conservative silence predicate for safe-cut traversal.
     *
     * Returns true ONLY when we have high-confidence evidence that the node is silent:
     *   - It has VAD analysis AND VAD reports no voice activity, OR
     *   - It is a pause / punctuation / deleted node (known silent by construction).
     *
     * If a node has no VAD data AND isn't inherently silent (e.g. an audio clip or
     * video clip inserted without analysis), we return false so safe-cut will NOT
     * walk across it and treat it as accumulated silence. This fixes the bug where
     * unanalyzed neighboring media silently looked like "safe to cut" silence.
     */
    isSequencerNodeKnownSilent : function(sequencerNode, vadConfigType){
        if(!sequencerNode){
            return false;
        }
        if(Utils.getInstance().isPauseSequencerNodeInstance(sequencerNode)
            || Utils.getInstance().isPunctuationSequencerNodeInstance(sequencerNode)
            || Utils.getInstance().isDeletedSequencerNodeInstance(sequencerNode)){
            return true;
        }
        const vadManager = VoiceActivityDetectionManager.getInstance();
        const configType = vadConfigType || vadManager.getVadConfigs().BALANCED;
        if(!sequencerNode.getVoiceAcitivityInfo(configType)){
            // Unknown — fail safe (refuse to treat as silence).
            return false;
        }
        return !sequencerNode.containsVoiceActivity(configType);
    },

    getNextSequencerNodeMatchingOneOfTheConditions(sequencerNode, conditionFunctions){
        const nextSequencerNode = sequencerNode.getNext();
        if(!nextSequencerNode){
            return sequencerNode;
        }
        for(let i =0; i < conditionFunctions.length; i++){
            const conditionFunction = conditionFunctions[i];
            if(conditionFunction(nextSequencerNode)){
                return nextSequencerNode;
            }
        }
        return this.getNextSequencerNodeMatchingOneOfTheConditions(nextSequencerNode, conditionFunctions);
    },

    getPreviousSequencerNodeMatchingOneOfTheConditions(sequencerNode, conditionFunctions){
        const previousSequencerNode = sequencerNode.getPrevious();
        if(!previousSequencerNode){
            return sequencerNode;
        }
        for(let i =0; i < conditionFunctions.length; i++){
            const conditionFunction = conditionFunctions[i];
            if(conditionFunction(previousSequencerNode)){
                return previousSequencerNode;
            }
        }
        return this.getPreviousSequencerNodeMatchingOneOfTheConditions(previousSequencerNode, conditionFunctions);
    },

    /**
     * ==================================================================================
     * SAFE TRIM FALLBACK SYSTEM - COMPREHENSIVE DOCUMENTATION
     * ==================================================================================
     * 
     * PROBLEM STATEMENT:
     * When automatically trimming audio segments (especially speech), we need to find
     * "safe" cut points that don't interrupt words or create audio artifacts. The
     * challenge is that Voice Activity Detection (VAD) is imperfect and can misidentify:
     * - Soft consonants ('th', 'f', 's', 'sh') as silence
     * - Word boundaries with gradual amplitude changes
     * - Breathing sounds and mouth noises between words
     * 
     * SOLUTION APPROACH:
     * Instead of using a single strict threshold, we implement a progressive fallback
     * strategy that tries multiple parameter sets in order from most conservative to
     * most permissive. This maximizes the chance of finding a safe cut while
     * maintaining audio quality.
     * 
     * BUFFER VALUES REASONING:
     * - 80ms buffer: Sits between intra-word gaps (20-50ms) and inter-word pauses (100-300ms)
     * - 1.5x ratio (120ms): Gets closer to natural inter-word pause territory
     * - 20ms minimum: Absolute minimum to avoid clicking artifacts
     * 
     * FALLBACK STRATEGIES:
     * 1. Standard: strict -> left-lenient -> right-lenient -> very permissive
     * 2. Speech-optimized: Multiple gradual steps with asymmetric biases
     * 3. Custom: Configurable options for specific use cases
     * 
     * USAGE EXAMPLES:
     * 
     * // Basic usage with current constants (80ms buffer)
     * const params = SequencerNodeUtils.getInstance().generateSafeTrimFallbackParameters(80, 80, 0, 0);
     * const result = SequencerNodeUtils.getInstance().getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodesWithFallback(nodes, params);
     * 
     * 
     * // Custom configuration
     * const customParams = SequencerNodeUtils.getInstance().generateSafeTrimFallbackParameters(80, 80, 0, 0, 2.0, {
     *     includeVeryPermissive: false,  // Skip the risky fallback
     *     includeAsymmetricFallbacks: true
     * });
     * 
     * PERFORMANCE IMPACT:
     * - Minimal: Only processes additional parameter sets if previous ones fail
     * - Most cuts succeed on first attempt (strict parameters)
     * - Fallbacks are only used for difficult cases
     * - Uses cached VAD data - no audio re-analysis
     * 
     * AUDIO QUALITY CONSIDERATIONS:
     * - Always prefers stricter parameters (better quality)
     * - Asymmetric fallbacks handle sentence beginnings/endings differently
     * - 20ms minimum prevents audible clicking artifacts
     * - Returns metadata about which parameter set was used for quality assessment
     * 
     * ==================================================================================
     */

    /**
     * Finds safe trim boundaries for an array of sequencer nodes by trying multiple parameter sets
     * in order of preference until one produces a safe cut result.
     * 
     * ALGORITHM:
     * 1. Validates input parameters
     * 2. Iterates through parameter sets in order (strict to permissive)
     * 3. For each set, calls getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodes
     * 4. Returns immediately when a safe cut is found (nodes.length > 0)
     * 5. If all sets fail, returns empty result with diagnostic information
     * 
     * PERFORMANCE CHARACTERISTICS:
     * - Best case: O(1) - first parameter set succeeds
     * - Worst case: O(n) where n = number of parameter sets
     * - Typical case: ~80% succeed on first attempt in speech content
     * - Uses cached VAD data - no audio re-analysis
     * 
     * RETURN VALUE ANALYSIS:
     * - result.nodes.length > 0: Safe cut found
     * - result.usedParameterSetIndex: Which fallback was used (0 = strict, higher = more permissive)
     * - result.usedParameterSet.description: Human-readable description of the strategy used
     * - result.allParameterSetsFailed: true if no safe cut could be found
     *   - totalParameterSetsAttempted: Number of parameter sets tried
     * 
     * QUALITY INDICATORS:
     * - Index 0: Highest quality (strict parameters)
     * - Index 1-2: Good quality (moderate fallbacks)
     * - Index 3+: Acceptable quality (permissive fallbacks)
     * - allParameterSetsFailed: Manual review recommended
     * 
     * @param {Array} arrayOfSequencerNodes - The array of nodes to find safe boundaries for
     * @param {Array} parameterSets - Array of parameter objects to try in order. Generate using:
     *                                generateSafeTrimFallbackParameters()
     * @returns {Object} Result object with the following structure:
     *   - nodes: Array of expanded nodes (empty if not safe to cut)
     *   - left: Left boundary info {node, accumulatedSilence, safeToCut}
     *   - right: Right boundary info {node, accumulatedSilence, safeToCut}
     *   - leftSilence: Left silence boundary info (if safe to cut)
     *   - rightSilence: Right silence boundary info (if safe to cut)
     *   - usedParameterSetIndex: Index of successful parameter set (0-based)
     *   - usedParameterSet: The actual parameter set that succeeded
     *   - allParameterSetsFailed: Boolean indicating if all attempts failed
     *   - totalParameterSetsAttempted: Number of parameter sets tried
     * 
     * // Log quality metrics for monitoring
     * if (result.nodes.length > 0) {
     *     const quality = result.usedParameterSetIndex === 0 ? 'optimal' : 
     *                    result.usedParameterSetIndex <= 2 ? 'good' : 'acceptable';
     *     console.log(`Trim quality: ${quality} (strategy: ${result.usedParameterSet.description})`);
     * }
     */
    getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodesWithFallback : function(arrayOfSequencerNodes, parameterSets) {
        // Validate inputs
        if (!arrayOfSequencerNodes || arrayOfSequencerNodes.length === 0) {
            return {
                nodes: [],
                left: null,
                right: null
            };
        }
        
        // Check if the array contains only non-word nodes (like pauses)
        // If so, they're inherently safe to cut
        if (!this.containsAnyWordSequencerNode(arrayOfSequencerNodes)) {
            const firstNode = arrayOfSequencerNodes[0];
            const lastNode = arrayOfSequencerNodes[arrayOfSequencerNodes.length - 1];
            
            return {
                nodes: arrayOfSequencerNodes,
                left: { 
                    node: firstNode, 
                    accumulatedSilence: 0, 
                    safeToCut: true 
                },
                right: { 
                    node: lastNode, 
                    accumulatedSilence: 0, 
                    safeToCut: true 
                },
                leftSilence: { 
                    node: firstNode, 
                    accumulatedSilence: 0, 
                    safeToCut: true 
                },
                rightSilence: { 
                    node: lastNode, 
                    accumulatedSilence: 0, 
                    safeToCut: true 
                },
                allPauseNodes: true,
                usedParameterSetIndex: -1,
                usedParameterSet: { description: "All pause nodes - inherently safe" }
            };
        }
        
        if (!parameterSets || parameterSets.length === 0) {
            // If no parameter sets provided, use default parameters
            return this.getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodes(arrayOfSequencerNodes);
        }
        
        // Try each parameter set in order
        for (let i = 0; i < parameterSets.length; i++) {
            const params = parameterSets[i];
            
            // Set defaults for any missing parameters
            const requiredSafetyLeft = params.requiredSafetyOnTheLeftInMilliseconds ?? 200;
            const requiredSafetyRight = params.requiredSafetyOnTheRightInMilliseconds ?? 200;
            const silenceBoundaryLeft = params.silenceBoundaryInMillisecondsOnTheLeft ?? 200;
            const silenceBoundaryRight = params.silenceBoundaryInMillisecondsOnTheRight ?? 200;
            
            const result = this.getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodes(
                arrayOfSequencerNodes,
                requiredSafetyLeft,
                requiredSafetyRight,
                silenceBoundaryLeft,
                silenceBoundaryRight
            );
            
            // If we found a safe cut (nodes array is not empty), return it
            if (result.nodes && result.nodes.length > 0) {
                return {
                    ...result,
                    // Add metadata about which parameter set was successful
                    usedParameterSetIndex: i,
                    usedParameterSet: params
                };
            }
        }
        
        // If no parameter set worked, return empty result with information about the last attempt
        const lastParams = parameterSets[parameterSets.length - 1];
        const lastResult = this.getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodes(
            arrayOfSequencerNodes,
            lastParams.requiredSafetyOnTheLeftInMilliseconds ?? 200,
            lastParams.requiredSafetyOnTheRightInMilliseconds ?? 200,
            lastParams.silenceBoundaryInMillisecondsOnTheLeft ?? 200,
            lastParams.silenceBoundaryInMillisecondsOnTheRight ?? 200
        );
        
        return {
            nodes: [],
            left: lastResult.left,
            right: lastResult.right,
            allParameterSetsFailed: true,
            totalParameterSetsAttempted: parameterSets.length
        };
    },

    /**
     * Finds safe trim boundaries for an array of sequencer nodes by expanding the selection
     * to include additional padding nodes based on silence thresholds.
     * 
     * @param {Array} arrayOfSequencerNodes - The array of nodes to find safe boundaries for
     * @param {number} requiredSafetyOnTheLeftInMilliseconds - Minimum silence needed on left for safe cut (default: 200ms)
     * @param {number} requiredSafetyOnTheRightInMilliseconds - Minimum silence needed on right for safe cut (default: 200ms)
     * @param {number} silenceBoundaryInMillisecondsOnTheLeft - Additional silence padding to include on left (default: 200ms)
     * @param {number} silenceBoundaryInMillisecondsOnTheRight - Additional silence padding to include on right (default: 200ms)
     * @returns {Object} Result object containing:
     *   - nodes: Array of expanded nodes (empty if not safe to cut)
     *   - left: Left boundary info {node, accumulatedSilence, safeToCut}
     *   - right: Right boundary info {node, accumulatedSilence, safeToCut}
     *   - leftSilence: Left silence boundary info (if safe to cut)
     *   - rightSilence: Right silence boundary info (if safe to cut)
     */
    getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodes : function(arrayOfSequencerNodes, requiredSafetyOnTheLeftInMilliseconds=200, requiredSafetyOnTheRightInMilliseconds=200, silenceBoundaryInMillisecondsOnTheLeft=200, silenceBoundaryInMillisecondsOnTheRight=200){
        const firstSequencerNode = arrayOfSequencerNodes[0];
        const lastSequencerNode = arrayOfSequencerNodes[arrayOfSequencerNodes.length - 1];
        
        // First, check if the selection meets the minimum safety requirements
        const safetyBoundaries = this.getSafeTrimSequencerNodesBoundaries(firstSequencerNode, lastSequencerNode, requiredSafetyOnTheLeftInMilliseconds, requiredSafetyOnTheRightInMilliseconds);
        
        // Only proceed if both left and right sides are safe to cut
        if(safetyBoundaries.left.safeToCut && safetyBoundaries.right.safeToCut){
            // Find silence boundaries for additional padding
            const silenceBoundaries = this.getSafeTrimSequencerNodesBoundaries(firstSequencerNode, lastSequencerNode, silenceBoundaryInMillisecondsOnTheLeft, silenceBoundaryInMillisecondsOnTheRight);
            
            // Collect nodes to add on the left side (from silence boundary to first node)
            const leftPaddingNodes = [];
            let currentLeftNode = silenceBoundaries.left.node;
            while(currentLeftNode && currentLeftNode !== firstSequencerNode){
                leftPaddingNodes.push(currentLeftNode);
                currentLeftNode = currentLeftNode.getNext();
            }
            
            // Collect nodes to add on the right side (from silence boundary to last node)
            const rightPaddingNodes = [];
            let currentRightNode = silenceBoundaries.right.node;
            while(currentRightNode && currentRightNode !== lastSequencerNode){
                rightPaddingNodes.push(currentRightNode);
                currentRightNode = currentRightNode.getPrevious();
            }
            // Reverse right padding nodes since we collected them backward
            rightPaddingNodes.reverse();
            
            return {
                nodes: leftPaddingNodes.concat(arrayOfSequencerNodes).concat(rightPaddingNodes),
                left: safetyBoundaries.left,
                right: safetyBoundaries.right,
                leftSilence: silenceBoundaries.left,
                rightSilence: silenceBoundaries.right
            };
        }
        
        // Return empty result if not safe to cut
        return {
            nodes: [],
            left: safetyBoundaries.left,
            right: safetyBoundaries.right
        };
    },

    /**
     * Finds safe trim boundaries for a range of sequencer nodes by analyzing silence
     * and voice activity patterns around the selection.
     * 
     * @param {Object} sequencerNodeStartOn - The starting node of the selection
     * @param {Object} sequencerNodeToEndOn - The ending node of the selection
     * @param {number} requiredSafetyOnTheLeftInMilliseconds - Minimum silence needed on left for safe cut (default: 200ms)
     * @param {number} requiredSafetyOnTheRightInMilliseconds - Minimum silence needed on right for safe cut (default: 200ms)
     * @returns {Object} Result object containing:
     *   - left: {node, accumulatedSilence, safeToCut} - Left boundary analysis
     *   - right: {node, accumulatedSilence, safeToCut} - Right boundary analysis
     */
    getSafeTrimSequencerNodesBoundaries : function(sequencerNodeStartOn, sequencerNodeToEndOn, requiredSafetyOnTheLeftInMilliseconds=200, requiredSafetyOnTheRightInMilliseconds=200){
        /**
         * Walks forward from the end node to find a safe right boundary.
         * Looks for sufficient silence, edit boundaries, or voice/audio events.
         */
        const findSafeRightBoundary = (function(startNode, requiredSilenceThreshold){
            let accumulatedSilence = 0;
            let currentNode = startNode.getNext();

            while (currentNode) {
                // Only accumulate silence when we have positive evidence the node is silent.
                // isSequencerNodeKnownSilent is conservative: unanalyzed audio/video clips
                // are NOT treated as silence, which prevents safe-cut from walking across
                // neighboring inserted media that happens to have no VAD data.
                if (this.isSequencerNodeKnownSilent(currentNode, VoiceActivityDetectionManager.VAD_CONFIGS.BALANCED) &&
                    !Utils.getInstance().isWordSequencerNodeInstance(currentNode) &&
                    !Utils.getInstance().isAudioEventSequencerNodeInstance(currentNode)) {

                    accumulatedSilence += currentNode.getAudioSegment().getDuration();

                    // If we've accumulated enough silence, this is a safe cut point
                    if (accumulatedSilence >= requiredSilenceThreshold) {
                        return { node: currentNode, accumulatedSilence, safeToCut: true };
                    }
                }

                // Stop if we hit an edit boundary or voice/audio content
                if (!currentNode.isThereContinuityOnRight() ||
                    Utils.getInstance().isWordSequencerNodeInstance(currentNode) ||
                    Utils.getInstance().isAudioEventSequencerNodeInstance(currentNode)) {
                    return { node: currentNode, accumulatedSilence, safeToCut: false };
                }

                currentNode = currentNode.getNext();
            }
            
            // Reached end of sequence without finding sufficient silence
            return { node: startNode, accumulatedSilence, safeToCut: false };
        }).bind(this);

        /**
         * Walks backward from the start node to find a safe left boundary.
         * Looks for sufficient silence, edit boundaries, or voice/audio events.
         */
        const findSafeLeftBoundary = (function(startNode, requiredSilenceThreshold){
            let accumulatedSilence = 0;
            let currentNode = startNode.getPrevious();

            while (currentNode) {
                // Only accumulate silence when we have positive evidence the node is silent.
                // See findSafeRightBoundary comment — same conservative policy applies here.
                if (this.isSequencerNodeKnownSilent(currentNode, VoiceActivityDetectionManager.VAD_CONFIGS.BALANCED) &&
                    !Utils.getInstance().isWordSequencerNodeInstance(currentNode) &&
                    !Utils.getInstance().isAudioEventSequencerNodeInstance(currentNode)) {

                    accumulatedSilence += currentNode.getAudioSegment().getDuration();

                    // If we've accumulated enough silence, this is a safe cut point
                    if (accumulatedSilence >= requiredSilenceThreshold) {
                        return { node: currentNode, accumulatedSilence, safeToCut: true };
                    }
                }

                // Stop if we hit an edit boundary or voice/audio content
                if (!currentNode.isThereContinuityOnLeft() ||
                    Utils.getInstance().isWordSequencerNodeInstance(currentNode) ||
                    Utils.getInstance().isAudioEventSequencerNodeInstance(currentNode)) {
                    return { node: currentNode, accumulatedSilence, safeToCut: false };
                }

                currentNode = currentNode.getPrevious();
            }
            
            // Reached beginning of sequence without finding sufficient silence
            return { node: startNode, accumulatedSilence, safeToCut: false };
        }).bind(this);

        // Find boundaries - always call the boundary functions to handle 0 values properly
        const rightBoundaryResult = findSafeRightBoundary(sequencerNodeToEndOn, requiredSafetyOnTheRightInMilliseconds);
        const leftBoundaryResult = findSafeLeftBoundary(sequencerNodeStartOn, requiredSafetyOnTheLeftInMilliseconds);

        return {
            left: leftBoundaryResult,
            right: rightBoundaryResult
        };
    },

    containsAnyWordSequencerNode: function(arrayOfSequencerNodes) {
        if (!arrayOfSequencerNodes || arrayOfSequencerNodes.length === 0) {
            return false;
        }
        return arrayOfSequencerNodes.some(node => 
            Utils.getInstance().isWordSequencerNodeInstance(node)
        );
    },

    getDefaultAllowedCategoryClassesForAudioClassifier : function(){
        return ALLOWED_SOUND_CLASSES;
    },

    getSoundGroups: function(){
        return SOUND_GROUPS;
    },

    /**
     * Generates a set of fallback parameters for safe trim operations.
     * Creates a progressive fallback strategy with multiple attempts for better cut success rates.
     * See comprehensive documentation above for detailed reasoning and approach.
     * 
     * @param {number} leftBuffer - Initial left safety buffer in milliseconds (recommended: 80ms)
     * @param {number} rightBuffer - Initial right safety buffer in milliseconds (recommended: 80ms)
     * @param {number} leftSilence - Initial left silence boundary in milliseconds (typically 0 for speech)
     * @param {number} rightSilence - Initial right silence boundary in milliseconds (typically 0 for speech)
     * @param {number} fallbackRatio - Multiplier for fallback attempts (recommended: 1.5 for speech, 2.0 for music)
     * @param {Object} options - Additional options for fallback strategy
     * @param {boolean} options.includeAsymmetricFallbacks - Include left-only and right-only attempts (default: true)
     *                                                      Useful for sentence beginnings/endings
     * @param {boolean} options.includeVeryPermissive - Include very permissive final attempt (default: true)
     *                                                  Risky but sometimes necessary for poor VAD
     * @param {number} options.veryPermissiveRatio - Ratio for very permissive attempt (default: 0.5)
     *                                               Lower = more permissive, higher = more conservative
     * @returns {Array} Array of parameter objects for fallback strategy, ordered from strict to permissive
     * 
     * @example
     * // Standard speech cutting with 80ms buffer
     * const params = generateSafeTrimFallbackParameters(80, 80, 0, 0, 1.5);
     * // Results in: [80ms strict, 120ms left, 120ms right, 40ms permissive]
     * 
     * @example
     * // Conservative approach without risky fallback
     * const params = generateSafeTrimFallbackParameters(80, 80, 0, 0, 1.5, {
     *     includeVeryPermissive: false
     * });
     * // Results in: [80ms strict, 120ms left, 120ms right]
     */
    generateSafeTrimFallbackParameters : function(leftBuffer, rightBuffer, leftSilence, rightSilence, fallbackRatio = 1.5, options = {}) {
        const {
            includeAsymmetricFallbacks = true,
            includeVeryPermissive = true,
            veryPermissiveRatio = 0.5
        } = options;

        const parameterSets = [
            // First attempt: Use original parameters (strict)
            {
                requiredSafetyOnTheLeftInMilliseconds: leftBuffer,
                requiredSafetyOnTheRightInMilliseconds: rightBuffer,
                silenceBoundaryInMillisecondsOnTheLeft: leftSilence,
                silenceBoundaryInMillisecondsOnTheRight: rightSilence,
                description: "Strict (original parameters)"
            }
        ];

       /* if (includeAsymmetricFallbacks) {
            // Second attempt: More lenient on the left side (good for cutting at sentence beginnings)
            parameterSets.push({
                requiredSafetyOnTheLeftInMilliseconds: Math.round(leftBuffer * fallbackRatio),
                requiredSafetyOnTheRightInMilliseconds: 0,
                silenceBoundaryInMillisecondsOnTheLeft: leftSilence,
                silenceBoundaryInMillisecondsOnTheRight: 0,
                description: `Lenient left (${Math.round(leftBuffer * fallbackRatio)}ms left only)`
            });

            // Third attempt: More lenient on the right side (good for cutting at sentence endings)
            parameterSets.push({
                requiredSafetyOnTheLeftInMilliseconds: 0,
                requiredSafetyOnTheRightInMilliseconds: Math.round(rightBuffer * fallbackRatio),
                silenceBoundaryInMillisecondsOnTheLeft: 0,
                silenceBoundaryInMillisecondsOnTheRight: rightSilence,
                description: `Lenient right (${Math.round(rightBuffer * fallbackRatio)}ms right only)`
            });
        }*/

        /*if (includeVeryPermissive) {
            // Fourth attempt: Very permissive - allows cuts with minimal silence (risky but sometimes necessary)
            // This accounts for VAD inaccuracies and soft consonants
            parameterSets.push({
                requiredSafetyOnTheLeftInMilliseconds: Math.round(leftBuffer * veryPermissiveRatio),
                requiredSafetyOnTheRightInMilliseconds: Math.round(rightBuffer * veryPermissiveRatio),
                silenceBoundaryInMillisecondsOnTheLeft: Math.round(leftSilence * veryPermissiveRatio),
                silenceBoundaryInMillisecondsOnTheRight: Math.round(rightSilence * veryPermissiveRatio),
                description: `Very permissive (${Math.round(leftBuffer * veryPermissiveRatio)}ms both sides)`
            });
        }*/

        return parameterSets;
    },

    /**
     * Finds optimal cut points using enhanced cut point analysis if available.
     * This leverages cached enhanced analysis results to find the best possible cut locations
     * within the safe boundaries determined by the fallback system.
     * 
     * ENHANCED CUT POINT STRATEGY:
     * 1. Use fallback system to determine safe boundaries
     * 2. If enhanced analysis is available, find optimal cut points within those boundaries
     * 3. Prefer high-confidence cut points (breathing pauses, natural pauses)
     * 4. Fall back to standard boundary detection if no optimal points found
     * 
     * @param {Array} arrayOfSequencerNodes - The array of nodes to find optimal cuts for
     * @param {Array} parameterSets - Fallback parameter sets (same as fallback system)
     * @returns {Object} Result object with optimal cut points and enhanced metadata
     * 
     * @example
     * // Enhanced cutting with optimal cut point detection
     * const fallbackParams = SequencerNodeUtils.getInstance().generateSafeTrimFallbackParameters(80, 80, 0, 0);
     * const result = SequencerNodeUtils.getInstance().getSafeTrimWithEnhancedCutPointAnalysis(nodes, fallbackParams);
     * 
     * if (result.nodes.length > 0) {
     *     console.log(`Cut quality: ${result.enhancedCutPointUsed ? 'Optimal' : 'Standard'}`);
     *     if (result.enhancedCutPointUsed) {
     *         console.log(`Cut reason: ${result.cutPointReason}`);
     *         console.log(`Cut confidence: ${result.cutPointConfidence}`);
     *     }
     * }
     */
    getSafeTrimWithEnhancedCutPointAnalysis : function(arrayOfSequencerNodes, parameterSets) {
        // First, get safe boundaries using the standard fallback system
        const standardResult = this.getSafeTrimSequencerNodesBoundariesFromArrayOfSequencerNodesWithFallback(arrayOfSequencerNodes, parameterSets);
        
        // If standard system failed, return early
        if (!standardResult.nodes || standardResult.nodes.length === 0) {
            return {
                ...standardResult,
                enhancedCutPointUsed: false,
                enhancedAnalysisAvailable: false
            };
        }
        
        // Check if enhanced cut point analysis is available for any of the nodes
        const firstNode = arrayOfSequencerNodes[0];
        const enhancedAnalysis = firstNode.getEnhancedCutPointAnalysisInfo();
        
        if (!enhancedAnalysis || !enhancedAnalysis.safeCutPoints || enhancedAnalysis.safeCutPoints.length === 0) {
            return {
                ...standardResult,
                enhancedCutPointUsed: false,
                enhancedAnalysisAvailable: !!enhancedAnalysis
            };
        }
        
        // Find optimal cut points within the safe boundaries
        const startTime = standardResult.left.node.getAudioSegment().getStartTime() / 1000; // Convert to seconds
        const endTime = standardResult.right.node.getAudioSegment().getEndTime() / 1000;
        
        // Find the best cut points within our safe boundaries
        const optimalCutPoints = enhancedAnalysis.safeCutPoints.filter(cutPoint => 
            cutPoint.timestamp >= startTime && cutPoint.timestamp <= endTime
        ).sort((a, b) => b.confidence - a.confidence); // Sort by confidence (highest first)
        
        if (optimalCutPoints.length > 0) {
            const bestCutPoint = optimalCutPoints[0];
            
            return {
                ...standardResult,
                enhancedCutPointUsed: true,
                enhancedAnalysisAvailable: true,
                cutPointTimestamp: bestCutPoint.timestamp,
                cutPointConfidence: bestCutPoint.confidence,
                cutPointReason: bestCutPoint.reason,
                cutPointSilenceDuration: bestCutPoint.silenceDuration,
                alternativeCutPoints: optimalCutPoints.slice(1, 3) // Include up to 2 alternatives
            };
        }
        
        // Enhanced analysis available but no optimal points in safe range
        return {
            ...standardResult,
            enhancedCutPointUsed: false,
            enhancedAnalysisAvailable: true,
            enhancedAnalysisNote: "Enhanced analysis available but no optimal cut points found within safe boundaries"
        };
    },



});


const sequencerNodeUtilsInstance = new SequencerNodeUtils();

export default {
	getInstance : function() {
		return sequencerNodeUtilsInstance;
	}
}; 